/**
 * cart-drawer-new.js
 * Cart drawer: open/close, update, quantity, remove, upsell, add to cart buttons.
 * Needs: jQuery, Slick, global `routes` object (cart_url, cart_add_url, cart_update_url).
 *
 * These functions are global on purpose, other theme files call them.
 * Do not rename: openCartDrawer, closeCartDrawer, updateCartDrawer,
 * updateCartItemCount, variantSelection.
 */

/* ------------------------------------------------------------------ */
/* Open / close                                                        */
/* ------------------------------------------------------------------ */

// Open Cart Drawer Function
function openCartDrawer() {
  var drawer = document.querySelector('.f-cart-drawer');
  if (drawer) drawer.classList.add('f-cart-drawer--active');

  // Close the quick view modal. It does not exist on every page, so check first.
  document.body.classList.remove('modal--open', 'modal-quickview--open', 'overflow-hidden');
  var quickview = document.querySelector('.ar_new_quickview');
  if (quickview) quickview.classList.remove('modal--active');

  variantSelection();
}

// Close Cart Drawer Function
function closeCartDrawer() {
  var drawer = document.querySelector('.f-cart-drawer');
  if (drawer) drawer.classList.remove('f-cart-drawer--active');
}

/* ------------------------------------------------------------------ */
/* Cart count                                                          */
/* ------------------------------------------------------------------ */

async function updateCartItemCount(count = 0) {
  var total = count;

  try {
    // Only ask the server when no count was passed in
    if (count == 0) {
      const res = await fetch(`${routes.cart_url}`, {
        method: 'get',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const cart = await res.json();
      total = cart.item_count;
    }
  } catch (error) {
    console.error('Cart count update failed:', error);
    return;
  }

  document.querySelectorAll('.cart-count-bubble').forEach((el) => {
    el.textContent = total;
    el.classList.toggle('empty_cart', total <= 0);
  });
}

/* ------------------------------------------------------------------ */
/* Update drawer                                                       */
/* ------------------------------------------------------------------ */

// Returns the promise, so `await updateCartDrawer()` really waits for the new HTML
function updateCartDrawer() {
  return fetch(`${routes.cart_url}?section_id=cart-drawer-new`)
    .then((res) => res.text())
    .then((data) => {
      const html = document.createElement('div');
      html.innerHTML = data;

      const newDrawer = html.querySelector('.f-cart-drawer');
      const drawer = document.querySelector('.f-cart-drawer');
      if (!newDrawer || !drawer) {
        console.warn('Cart drawer: .f-cart-drawer not found in section response');
        return;
      }

      drawer.innerHTML = newDrawer.innerHTML;

      reinitializeSlickSlider();
      addCartDrawerListeners();
      removeCartDrawerListeners();
      addCartUpsellListeners();
      variantSelection();
    })
    .catch((error) => {
      console.error('Cart drawer update failed:', error);
    });
}

// Sends new quantities to the cart ({ lineKeyOrVariantId: quantity }), then refreshes count and drawer
async function cartDrawerUpdateLines(updates) {
  const res = await fetch(`${routes.cart_update_url}`, {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ updates: updates })
  });
  const cart = await res.json();
  updateCartItemCount(cart.item_count);

  await updateCartDrawer();
}

/* ------------------------------------------------------------------ */
/* Quantity + close listeners                                          */
/* ------------------------------------------------------------------ */

function addCartDrawerListeners() {
  // update Quantities
  document.querySelectorAll('.f-cart-drawer-quantity-selector button').forEach((button) => {
    button.addEventListener('click', async () => {
      const icon = button.querySelector('.icon');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (icon) icon.classList.add('hidden');
      if (spinner) spinner.classList.remove('hidden');

      try {
        const item = button.closest('.f-cart-drawer-item');
        const key = item ? item.getAttribute('data-line-item-key') : null;
        if (!key) return;

        const currentQuantity = Number(button.parentElement.querySelector('input').value);
        const isUp = button.classList.contains('f-cart-drawer-quantity-selector-plus');
        const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;

        await cartDrawerUpdateLines({ [key]: newQuantity });
      } catch (error) {
        console.error('Cart quantity update failed:', error);
      } finally {
        if (icon) icon.classList.remove('hidden');
        if (spinner) spinner.classList.add('hidden');
      }
    });
  });

  // Clicks inside the drawer box must not close the drawer
  const box = document.querySelector('.f-cart-drawer-box');
  if (box) {
    box.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  // Close Cart. .f-cart-drawer itself is never replaced, so bind it only once,
  // otherwise it gets one more listener on every cart update.
  document.querySelectorAll('.f-cart-drawer-header-right-close, .f-cart-drawer').forEach((el) => {
    if (el.dataset.dlCloseBound) return;
    el.dataset.dlCloseBound = '1';
    el.addEventListener('click', (e) => {
      e.preventDefault();
      closeCartDrawer();
    });
  });
}

/* ------------------------------------------------------------------ */
/* Remove item                                                         */
/* ------------------------------------------------------------------ */

function removeCartDrawerListeners() {
  document.querySelectorAll('.f-cart-drawer-item-main-flex-right button').forEach((button) => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();

      // Ignore double clicks while a remove request is running
      if (button.classList.contains('is-loading')) return;

      const item = button.closest('.f-cart-drawer-item');
      const key = item ? item.getAttribute('data-line-item-key') : null;
      if (!key) return;

      // Loading icon: CSS shows a spinner on .f-remove-item.is-loading
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        await cartDrawerUpdateLines({ [key]: 0 });
      } catch (error) {
        console.error('Cart remove failed:', error);
      } finally {
        // On success the drawer was re-rendered and this button is gone, nothing to reset.
        // On failure this brings the button back.
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    });
  });
}

/* ------------------------------------------------------------------ */
/* Upsell                                                              */
/* ------------------------------------------------------------------ */

function addCartUpsellListeners() {
  document.querySelectorAll('.add.f-upsale-add').forEach((button) => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();

      const label = button.querySelector('span');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (label) label.classList.add('hidden');
      if (spinner) spinner.classList.remove('hidden');

      try {
        // variant id of the upsell product
        const key = button.getAttribute('data-variant-id');

        await cartDrawerUpdateLines({ [key]: 1 });
      } catch (error) {
        console.error('Cart upsell add failed:', error);
      } finally {
        if (label) label.classList.remove('hidden');
        if (spinner) spinner.classList.add('hidden');
      }
    });
  });
}

// select change button id
function variantSelection() {
  document.querySelectorAll('.f-upsale-main select').forEach((select) => {
    // openCartDrawer() and updateCartDrawer() both call this, so bind only once
    if (select.dataset.dlBound) return;
    select.dataset.dlBound = '1';

    select.addEventListener('change', () => {
      const parentDiv = select.closest('.f-upsale-main');
      const btn = parentDiv ? parentDiv.querySelector('.f-upsale-add') : null;
      if (btn) btn.setAttribute('data-variant-id', select.value);
    });
  });
}

/* ------------------------------------------------------------------ */
/* Add to cart buttons (product page, quick add)                       */
/* ------------------------------------------------------------------ */

function bindAjaxAddToCart(options) {
  document.querySelectorAll(options.buttonSelector).forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      // Buttons that open the quick view are handled in dl_script.js
      if (options.skipClass && btn.classList.contains(options.skipClass)) return;

      e.preventDefault();

      // Buttons without a form (products with options) have nothing to add here
      const form = btn.closest('form');
      if (!form) return;

      const spinner = form.querySelector(`${options.buttonSelector} .loading-overlay__spinner`);
      const label = form.querySelector(`${options.buttonSelector} ${options.labelSelector}`);
      if (spinner) spinner.classList.remove('hidden');
      if (label) label.classList.add('hidden');

      try {
        if (form.action.endsWith('/cart/add')) {
          // Submit form with AJAX
          await fetch(`${routes.cart_add_url}`, {
            method: 'post',
            body: new FormData(form)
          });
          await updateCartDrawer();
          await updateCartItemCount();
          openCartDrawer();
        }
      } catch (error) {
        console.error(options.errorMessage, error);
      } finally {
        // spinner end, also when the request failed
        if (spinner) spinner.classList.add('hidden');
        if (label) label.classList.remove('hidden');
      }
    });
  });
}

/* ------------------------------------------------------------------ */
/* Initial binding                                                     */
/* ------------------------------------------------------------------ */

addCartDrawerListeners();
removeCartDrawerListeners();
addCartUpsellListeners();

// Add to cart on the product page
bindAjaxAddToCart({
  buttonSelector: '.product-form__submit',
  labelSelector: '.hide_for_load',
  errorMessage: 'Error adding to cart:'
});

// Quick add button on product cards
bindAjaxAddToCart({
  buttonSelector: '.quick-add__submit',
  labelSelector: '.q_btn',
  skipClass: 'quick-add__submit_options',
  errorMessage: 'Error updating cart:'
});

// Cart icon opens the drawer
document.querySelectorAll('#cart-icon-bubble').forEach((a) => {
  a.addEventListener('click', async (e) => {
    e.preventDefault();

    await updateCartDrawer();
    openCartDrawer();
    await updateCartItemCount();
  });
});

/* ------------------------------------------------------------------ */
/* jQuery handlers                                                     */
/* ------------------------------------------------------------------ */

// Writes the price of the selected option into the price block of the card
function updateUpsellPrice($select, $priceSection) {
  var $option = $select.find('option:selected');
  var salePrice = $option.attr('data-price');
  var comparePrice = $option.attr('data-compare-price');

  if (salePrice) {
    $priceSection.find('.actual-price').html(salePrice);
  }
  if (comparePrice) {
    $priceSection.find('.discount-price').html(comparePrice);
  }
}

$(function () {
  // Fallback close handler. Clicks inside the drawer box normally never reach body.
  $('body').on('click', '.f-cart-drawer-header-right-close', function () {
    closeCartDrawer();
  });

  // Slider cards: the price block is a sibling of .var_btn
  $('body').on('change', '.variant_selector_cart', function () {
    var $select = $(this);
    updateUpsellPrice($select, $select.closest('.var_btn').siblings('.price-section'));
  });

  // List cards: the price block is a sibling of the select
  $('body').on('change', '.variant_selector_cart_list', function () {
    var $select = $(this);
    updateUpsellPrice($select, $select.siblings('.price-section'));
  });
});

/* ------------------------------------------------------------------ */
/* Upsell slider                                                       */
/* ------------------------------------------------------------------ */

// Waits up to 5 seconds for .cart_upsell_slide, initializes Slick once, then stops.
// The old version kept polling every 500 ms forever when the slider did not exist.
function reinitializeSlickSlider() {
  var tries = 0;

  var timer = setInterval(function () {
    tries++;

    var $slides = $('.cart_upsell_slide').not('.slick-initialized');
    if ($slides.length) {
      if ($.fn.slick) {
        $slides.slick({
          dots: false,
          infinite: false,
          speed: 400,
          slidesToShow: 1,
          slidesToScroll: 1,
          prevArrow: '<button style="z-index: 1;" type="button" class="slick-prev"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 19" fill="none"><path d="M5.36816 10.3827C4.87988 9.89809 4.87988 9.11107 5.36816 8.62645L11.6182 2.42337C12.1064 1.93875 12.8994 1.93875 13.3877 2.42337C13.876 2.90798 13.876 3.695 13.3877 4.17962L8.02051 9.50652L13.3838 14.8334C13.8721 15.318 13.8721 16.105 13.3838 16.5897C12.8955 17.0743 12.1025 17.0743 11.6143 16.5897L5.36426 10.3866L5.36816 10.3827Z" fill="black" fill-opacity="0.76"/></svg></button>',
          nextArrow: '<button type="button" class="slick-next"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 19" fill="none"><path d="M14.6318 10.3827C15.1201 9.89809 15.1201 9.11107 14.6318 8.62645L8.38184 2.42337C7.89355 1.93875 7.10059 1.93875 6.6123 2.42337C6.12402 2.90798 6.12402 3.695 6.6123 4.17962L11.9795 9.50652L6.61621 14.8334C6.12793 15.318 6.12793 16.105 6.61621 16.5897C7.10449 17.0743 7.89746 17.0743 8.38574 16.5897L14.6357 10.3866L14.6318 10.3827Z" fill="black" fill-opacity="0.76"/></svg></button>'
        });
      }
      clearInterval(timer);
      return;
    }

    if (tries >= 10) clearInterval(timer);
  }, 500);
}
